import { apiClient } from './apiClient';
import {
  BoloItem,
  CreateBoloItemRequest,
  UpdateBoloItemRequest,
  BoloExport,
  PublicBolo
} from '../types';

/**
 * Service for BOLO (Be On The Lookout) management
 */
export class BoloService {
  /**
   * Get all BOLO items for a site
   */
  async getBoloItems(siteId: string): Promise<BoloItem[]> {
    try {
      const response = await apiClient.get<BoloItem[]>(`/bolo/site/${siteId}`);

      if (response.success && response.data) {
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching BOLO items:', error);
    }

    return [];
  }

  /**
   * Add a new BOLO item to a site
   */
  async createBoloItem(siteId: string, data: CreateBoloItemRequest): Promise<BoloItem> {
    const response = await apiClient.post<BoloItem>(`/bolo/site/${siteId}`, data);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.message || 'Failed to create BOLO item');
  }

  /**
   * Update a BOLO item (Admin/Manager only)
   */
  async updateBoloItem(id: string, data: UpdateBoloItemRequest): Promise<void> {
    const response = await apiClient.put<void>(`/bolo/${id}`, data);

    if (!response.success) {
      throw new Error(response.message || 'Failed to update BOLO item');
    }
  }

  /**
   * Delete a BOLO item
   */
  async deleteBoloItem(id: string): Promise<void> {
    const response = await apiClient.delete<void>(`/bolo/${id}`);

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete BOLO item');
    }
  }

  /**
   * Create or get the shareable BOLO export link for a site (Admin/Manager only)
   */
  async createOrGetBoloExport(siteId: string): Promise<BoloExport> {
    const response = await apiClient.post<BoloExport>(`/bolo/site/${siteId}/export`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.message || 'Failed to create BOLO export');
  }

  /**
   * Get public BOLO data by token (no auth required)
   */
  async getPublicBolo(token: string): Promise<PublicBolo> {
    const response = await apiClient.get<PublicBolo>(`/bolo/export/${token}`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.message || 'Failed to fetch public BOLO data');
  }
}

// Create a default instance
export const boloService = new BoloService();

export default boloService;
